import { pickedFile } from '../../../utils/pickedFile.js'
import MediaCaptureField from '../../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import Alert from '../../common/Alert';
import SelectField from '../../common/SelectField.js';
import companySubscriptionService from '../../../services/CompanySubscriptionService';
import { X } from 'lucide-react';
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js';
import Button from '../../common/Button.js';
import { useTranslation } from 'react-i18next';


/**
 * Path: src/components/company-subscription/subscription_payment/EditPaymentModal.js
 *
 * Props:
 *   payment       – payment object (from to_dict)
 *   subscription  – full subscription object
 *   totalPaid     – total completed payments (excluding this payment if completed)
 *   onClose       – fn
 *   onSuccess     – fn
 */
const EditPaymentModal = ({ payment, subscription, totalPaid, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    amount:               '',
    payment_method:       'cash',
    payment_status:       'pending',
    payment_date:         '',
    transaction_id:       '',
    transaction_reference: '',
    bank_name:            '',
    cheque_number:        '',
    upi_id:               '',
    notes:                '',
    remarks:              '',
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading]         = useState(false);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
  };

  const paymentMethods = [
    { value: 'cash',          label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque',        label: 'Cheque' },
    { value: 'upi',           label: 'UPI' },
    { value: 'credit_card',   label: 'Credit Card' },
    { value: 'debit_card',    label: 'Debit Card' },
    { value: 'net_banking',   label: 'Net Banking' },
    { value: 'other',         label: 'Other' },
  ];

  const paymentStatuses = [
    { value: 'pending',   label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed',    label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  useEffect(() => {
    if (payment) {
      setFormData({
        amount:               payment.amount ?? '',
        payment_method:       payment.payment_method       || 'cash',
        payment_status:       payment.payment_status       || 'pending',
        payment_date:         payment.payment_date ? payment.payment_date.split('T')[0] : '',
        transaction_id:       payment.transaction_id       || '',
        transaction_reference: payment.transaction_reference || '',
        bank_name:            payment.bank_name            || '',
        cheque_number:        payment.cheque_number        || '',
        upi_id:               payment.upi_id               || '',
        notes:                payment.notes                || '',
        remarks:              payment.remarks              || '',
      });
    }
  }, [payment]);

  // Max = subscription amount − (totalPaid − this payment's amount if it was completed)
  const getMaxAmount = () => {
    const subscriptionAmount = parseFloat(subscription?.subscription_amount || 0);
    const thisPaymentAmount  = parseFloat(payment?.amount || 0);
    const isCompleted = payment?.payment_status === 'completed';

    // If original was completed, it was counted in totalPaid — add it back for headroom
    const paidExcludingThis = isCompleted
      ? parseFloat(totalPaid || 0) - thisPaymentAmount
      : parseFloat(totalPaid || 0);

    const remaining = Math.max(0, subscriptionAmount - paidExcludingThis);
    return remaining.toFixed(2);
  };

  const showExtraFields = {
    bank_transfer: ['bank_name', 'transaction_id', 'transaction_reference'],
    cheque:        ['bank_name', 'cheque_number'],
    upi:           ['upi_id', 'transaction_id'],
    credit_card:   ['transaction_id', 'transaction_reference'],
    debit_card:    ['transaction_id', 'transaction_reference'],
    net_banking:   ['bank_name', 'transaction_id'],
    online:        ['transaction_id', 'transaction_reference'],
  };

  const extraFields = showExtraFields[formData.payment_method] || [];

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleFileChange = (e) => {
    const file = pickedFile(e);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { Alert.error('File must be under 5 MB'); return; }
    setReceiptFile(file);
    if (file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => setFilePreview(r.result);
      r.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setReceiptFile(null);
    setFilePreview(null);
    const el = document.getElementById('edit-sub-pay-file');
    if (el) el.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const max     = parseFloat(getMaxAmount());
    const entered = parseFloat(formData.amount);

    if (entered > max) {
      Alert.error(`Amount exceeds maximum allowed: ₹${max.toLocaleString('en-IN')}`); return;
    }

    setLoading(true);
    try {
      const paymentData = {
        amount:                formData.amount,
        payment_method:        formData.payment_method,
        payment_status:        formData.payment_status,
        payment_date:          formData.payment_date,
        transaction_id:        formData.transaction_id        || undefined,
        transaction_reference: formData.transaction_reference  || undefined,
        bank_name:             formData.bank_name             || undefined,
        cheque_number:         formData.cheque_number         || undefined,
        upi_id:                formData.upi_id                || undefined,
        notes:                 formData.notes                 || undefined,
        remarks:               formData.remarks               || undefined,
      };

      Object.keys(paymentData).forEach(k => paymentData[k] === undefined && delete paymentData[k]);

      await companySubscriptionService.updatePayment(payment.id, paymentData, receiptFile);
      Alert.success('Payment updated successfully!');
      onSuccess();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  const max = getMaxAmount();
  const inputCls = "w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm";
  const labelCls = "block text-sm font-medium text-secondary mb-1.5";

  const statusBadge = {
    pending:   'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    failed:    'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-700',
    refunded:  'bg-blue-100 text-blue-700',
  }[payment?.payment_status] || 'bg-gray-100 text-gray-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-secondary">Edit Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Summary card (read-only) */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Payment ID</p>
                <p className="font-semibold">{payment?.payment_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Subscription</p>
                <p className="font-semibold truncate">{subscription?.subscription_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${statusBadge}`}>
                  {payment?.payment_status?.charAt(0).toUpperCase() + payment?.payment_status?.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Original Amount</p>
                <p className="font-bold text-orange-600">₹{parseFloat(payment?.amount || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                className={`${inputCls} ${errors.amount ? 'border-red-400' : ''}`}
                required
              />
              <ValidationMessage error={errors.amount} />
              <p className="text-xs text-gray-500 mt-1">
                Max allowed: <span className="font-semibold text-orange-600">₹{parseFloat(max).toLocaleString('en-IN')}</span>
              </p>
            </div>
            <div>
              <label className={labelCls}>Payment Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                className={`${inputCls} ${errors.payment_date ? 'border-red-400' : ''}`}
                required
              />
              <ValidationMessage error={errors.payment_date} />
            </div>
          </div>

          {/* Method + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payment Method <span className="text-red-500">*</span></label>
              <SelectField variant="form" name="payment_method" value={formData.payment_method} onChange={handleChange} className={inputCls}>
                {paymentMethods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className={labelCls}>Payment Status <span className="text-red-500">*</span></label>
              <SelectField variant="form" name="payment_status" value={formData.payment_status} onChange={handleChange} className={inputCls}>
                {paymentStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </SelectField>
            </div>
          </div>

          {/* Conditional fields */}
          {extraFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {extraFields.includes('transaction_id') && (
                <div>
                  <label className={labelCls}>Transaction ID</label>
                  <input type="text" name="transaction_id" value={formData.transaction_id}
                    onChange={handleChange} placeholder="Transaction ID" className={inputCls} />
                </div>
              )}
              {extraFields.includes('transaction_reference') && (
                <div>
                  <label className={labelCls}>Transaction Reference</label>
                  <input type="text" name="transaction_reference" value={formData.transaction_reference}
                    onChange={handleChange} placeholder="UTR / Reference no." className={inputCls} />
                </div>
              )}
              {extraFields.includes('bank_name') && (
                <div>
                  <label className={labelCls}>Bank Name</label>
                  <input type="text" name="bank_name" value={formData.bank_name}
                    onChange={handleChange} placeholder="Bank name" className={inputCls} />
                </div>
              )}
              {extraFields.includes('cheque_number') && (
                <div>
                  <label className={labelCls}>Cheque Number</label>
                  <input type="text" name="cheque_number" value={formData.cheque_number}
                    onChange={handleChange} placeholder="Cheque number" className={inputCls} />
                </div>
              )}
              {extraFields.includes('upi_id') && (
                <div>
                  <label className={labelCls}>UPI ID</label>
                  <input type="text" name="upi_id" value={formData.upi_id}
                    onChange={handleChange} placeholder="UPI ID / VPA" className={inputCls} />
                </div>
              )}
            </div>
          )}

          {/* Notes + Remarks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                rows="2" placeholder="Internal notes..." className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Remarks</label>
              <textarea name="remarks" value={formData.remarks} onChange={handleChange}
                rows="2" placeholder="Optional remarks..." className={inputCls} />
            </div>
          </div>

          {/* Receipt */}
          <div>
            <label className={labelCls}>Update Receipt (Optional)</label>
            {payment?.has_receipt && !receiptFile && (
              <div className="mb-2">
                <a
                  href={payment.receipt_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm">
                  📎 View Current Receipt
                </a>
              </div>
            )}
            <MediaCaptureField mode="document" onPick={handleFileChange} />
            <p className="text-xs text-gray-500 mt-1">
              {payment?.has_receipt ? 'Upload to replace existing' : 'Images or PDF, max 5 MB'}
            </p>
            {filePreview && (
              <div className="mt-3 relative inline-block">
                <img src={filePreview} alt="Preview" className="w-28 h-28 object-cover border rounded-md" />
                <button type="button" onClick={removeFile}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">×</button>
              </div>
            )}
            {receiptFile && !filePreview && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                📄 {receiptFile.name}
                <button type="button" onClick={removeFile} className="text-red-600 text-xs">Remove</button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="primary" type="submit" loading={loading} className="flex-1">
              {loading ? 'Updating...' : 'Update Payment'}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal;